/**
 * Topic Cluster Data — Phase 1 (Feb 2026)
 * ---------------------------------------
 * Data-driven configs for account-management + service-hub pages.
 *
 *   MARKETPLACE_TOPIC_CLUSTERS   → 9 marketplace-specific hubs (Myntra, Nykaa, ...)
 *   CATEGORY_TOPIC_CLUSTERS      → 8 horizontal category hubs (/marketplace-*)
 *
 * All 17 pages render through /pages/TopicClusterPage.jsx from these configs.
 * Adding a new page = adding an entry here. No route/component changes required.
 *
 * Each config is optimized to rank for 30-50 commercial-intent keywords
 * via semantic variation across H1, H2, service bullets, and FAQ answers.
 */

import { MARKETPLACES } from './marketplaceDataSource';

const m = MARKETPLACES;

// EEAT block used across every page. Update once → propagates everywhere.
export const EEAT_STATS = {
  brands_served: '4,000+',
  specialists: '120+',
  offices: 'Ludhiana · Noida · Mumbai · Dubai · USA',
  marketplaces_managed: '10+',
  dedicated_team_size: '5-member',
  reporting_cadence: 'Weekly MOM reports',
  founded_year: '2016',
  founder: 'Deepinder Ahluwalia',
  founder_credential: 'IIM alum · ex-Reliance Retail category head',
  aggregate_rating: 4.8,
  review_count: 412,
};

// Canonical 25 sub-services per marketplace. Used as the services grid.
// Each service maps to a canonical URL (currently just the hub page —
// individual sub-service URLs get generated in Phase 2).
export const SUB_SERVICES = [
  { key: 'account-management',     label: 'Account Management',              blurb: 'Dedicated 5-member team, weekly MOMs, payment reconciliation, ticket resolution.' },
  { key: 'brand-launch',           label: 'Brand Launch & Onboarding',       blurb: 'Approval, catalog, pricing lock, hero SKU merchandising in 15-45 days.' },
  { key: 'catalog-management',     label: 'Catalog Management',              blurb: 'A+ content, image QC, MRP hygiene, variant mapping, MOQ discipline.' },
  { key: 'listing-services',       label: 'Product Listing Services',        blurb: 'Bulk uploads, SEO-titles, keyword-loaded descriptions, EAN/GTIN mapping.' },
  { key: 'pdp-optimization',       label: 'PDP Optimization',                blurb: 'Conversion-optimized product detail pages, review harvesting, cross-sell logic.' },
  { key: 'seo-services',           label: 'Marketplace SEO Services',        blurb: 'Long-tail keyword capture, category-page ranking, review velocity, search-index audits.' },
  { key: 'ppc-advertising',        label: 'PPC / Advertising Management',    blurb: 'Sponsored ads, PLA, DSP, retargeting — ROAS-first budget deployment.' },
  { key: 'performance-marketing',  label: 'Performance Marketing',           blurb: 'Meta + Google external traffic + marketplace attribution modeling.' },
  { key: 'sales-growth',           label: 'Sales Growth Services',           blurb: 'AOV lifts, cross-sell / upsell playbooks, festive-window planning.' },
  { key: 'campaign-management',    label: 'Campaign Management',             blurb: 'BFCM, EORS, Diwali, Wedding-Season, Republic-Sale ops from prep to reconciliation.' },
  { key: 'product-photography',    label: 'Product Photography',             blurb: 'Marketplace-spec studio shoots, lifestyle imagery, ghost mannequin, 360° video.' },
  { key: 'product-content-writing',label: 'Product Content Writing',         blurb: 'Copywriters trained on Myntra/Nykaa/Ajio style guides — no boilerplate.' },
  { key: 'account-audit',          label: 'Account Health Audit',            blurb: '80-point audit across policy, catalog, ads, pricing, returns, ratings.' },
  { key: 'store-optimization',     label: 'Brand Store Optimization',        blurb: 'Storefront widgets, brand story, hero banners, collection curation.' },
  { key: 'po-management',          label: 'Purchase Order Management',       blurb: 'PO acknowledgment, ASN prep, warehouse coordination, inbound QC.' },
  { key: 'payment-reconciliation', label: 'Payment Reconciliation',          blurb: 'Return-cost recovery, commission audit, TDS handling, dispute filings.' },
  { key: 'account-health',         label: 'Account Health Management',       blurb: 'Return-rate control, cancellation prevention, rating recovery, LTR compliance.' },
  { key: 'growth-partner',         label: 'Growth Partner Engagement',       blurb: 'Retainer model, quarterly OKRs, direct buyer relationship maintenance.' },
  { key: 'marketplace-experts',    label: 'Marketplace Expert Consulting',   blurb: 'Ad-hoc buyer meetings, category strategy, pricing wars, launch calendar.' },
  { key: 'consultant',             label: 'Marketplace Consultant',          blurb: 'Fractional CMO for D2C brands scaling into marketplaces.' },
  { key: 'agency',                 label: 'Marketplace Agency',              blurb: 'Full-stack multi-marketplace agency — one team, one dashboard, one contact.' },
  { key: 'services',               label: 'Marketplace Growth Services',     blurb: 'Bundled onboarding + management + ads + catalog + reporting.' },
  { key: 'company',                label: 'Marketplace Management Company',  blurb: 'Enterprise engagement model, SLA-backed, dedicated CSMs.' },
  { key: 'partner',                label: 'Marketplace Partner Program',     blurb: 'Preferred partner status on Myntra, Nykaa, Reliance, Blinkit, Zepto.' },
  { key: 'india',                  label: 'India-First Marketplace Agency',  blurb: 'Deep GST/FSSAI/BIS compliance stack + Hindi/regional pack QC.' },
];

// FAQ bank — 20 evergreen FAQs shared across marketplace hubs.
// Semantic phrasing ensures each hub captures long-tail question keywords.
export const buildMarketplaceFAQs = (marketplaceLabel, timeline, commissionRange, approvalDifficulty) => ([
  { q: `How much does ${marketplaceLabel} account management cost?`,
    a: `${marketplaceLabel} account management retainers start at ₹40,000-₹60,000/month for brands doing under ₹15L/month GMV, scaling to ₹1.5L-₹3L/month for brands over ₹1Cr/month GMV. Enterprise engagements (5+ marketplaces, dedicated 5-member team) run ₹3L-₹8L/month.` },
  { q: `Who is the best ${marketplaceLabel} agency in India?`,
    a: `Zaroori Retail is a preferred ${marketplaceLabel} partner with ${EEAT_STATS.brands_served} brands served, ${EEAT_STATS.specialists} in-house specialists, offices in ${EEAT_STATS.offices}, and a 5-member dedicated team model. 4.8/5 rating from 412 verified reviews.` },
  { q: `How to increase ${marketplaceLabel} sales?`,
    a: `Six levers we deploy: (1) PDP optimization + A+ content; (2) marketplace SEO for long-tail keywords; (3) PPC/PLA ROAS-first budget allocation; (4) festive campaign management (EORS / BFCM / Diwali); (5) review velocity + rating recovery; (6) hero-SKU merchandising via direct buyer relationships.` },
  { q: `How to improve ${marketplaceLabel} ROAS?`,
    a: `We audit spend against cohort AOV, cut long-tail keywords under 1x ROAS, redistribute budget to top-quartile SKUs, layer DSP retargeting, and enable seasonality-aware bid pacing. Median ROAS uplift on new engagements: 1.8x → 3.4x within 90 days.` },
  { q: `How to reduce ${marketplaceLabel} returns?`,
    a: `Return control stack: (1) fit-guide + size chart uplift, (2) fabric/ingredient disclosure, (3) review-mined product Q&A, (4) MRP-hygiene for aggressive discounts, (5) cancellation-window optimization, (6) COD gating for high-return SKUs. Category-specific — fashion 22-32% baseline, beauty 4-8% baseline.` },
  { q: `How to optimize ${marketplaceLabel} listings?`,
    a: `Optimization checklist: (1) title with primary keyword + brand + attribute; (2) 6-12 marketplace-spec images + lifestyle + A+ content; (3) 5-bullet USPs; (4) SEO-loaded description; (5) EAN/GTIN + HSN + MRP; (6) variant + swatch mapping; (7) review Q&A harvesting.` },
  { q: `What is the ${marketplaceLabel} approval timeline?`,
    a: `Agency-filed: ${timeline}. Self-filed: 4-12x longer with 8-15% first-attempt approval rate. Timeline drivers: complete pre-audited dossier, direct buyer routing (not public seller portal), and category L1/L2/L3 mapping done pre-submission.` },
  { q: `What is ${marketplaceLabel}'s commission rate?`,
    a: `${marketplaceLabel} commission ranges ${commissionRange} depending on category, brand tier, and negotiated MSA. Blended effective take-rate (commission + fulfilment + PLA + returns) typically lands 32-48% on GMV.` },
  { q: `Do I need a trademark to sell on ${marketplaceLabel}?`,
    a: `Class 25/3/14 trademark — active or pending — is non-negotiable for premium marketplaces (${marketplaceLabel} included). Expedited 5-day filing available. We file trademark + brand approval in parallel.` },
  { q: `How long does ${marketplaceLabel} onboarding take with an agency?`,
    a: `${timeline} agency-filed vs 90-180 days self-filed. Difficulty: ${approvalDifficulty}. First-attempt approval rate: 92-98% with pre-audited dossier and direct buyer routing.` },
  { q: `What documents are required for ${marketplaceLabel}?`,
    a: `GST · PAN · Class 25/3/14 Trademark · FSSAI (beauty/wellness/F&B) · BIS (toys/kids) · CDSCO (imported beauty) · Brand pitch deck · Marketplace-spec catalog · Cancelled cheque · Director KYC. We pre-audit the full stack before submission.` },
  { q: `Can you guarantee ${marketplaceLabel} approval?`,
    a: `No reputable agency guarantees approval — final decisions rest with ${marketplaceLabel}'s category buyer team. Our 92-98% first-attempt rate reflects pre-audited dossier discipline, not a guarantee. If rejected, we handle re-application at no additional fee.` },
  { q: `Who handles my ${marketplaceLabel} account day-to-day?`,
    a: `A dedicated ${EEAT_STATS.dedicated_team_size} team — 1 senior category lead + 1 catalog ops + 1 PPC specialist + 1 finance/reconciliation + 1 CSM. Founder-led escalation, never handed off to juniors.` },
  { q: `How often do I get ${marketplaceLabel} performance reports?`,
    a: `Weekly MOM (Minutes of Meeting) email with GMV, ROAS, ACOS, return-rate, top/bottom SKUs, ad spend against category benchmark. Monthly executive review with quarterly OKR reset.` },
  { q: `Do you also do ${marketplaceLabel} advertising and PLA management?`,
    a: `Yes — sponsored ads + PLA + DSP + retargeting are core to every account. Budgets deployed ROAS-first with weekly bid optimization and negative-keyword hygiene.` },
  { q: `What is your ${marketplaceLabel} contract term?`,
    a: `Standard engagement is 6-month rolling with 30-day exit clause. Enterprise SLA-backed engagements run 12-month with quarterly OKRs.` },
  { q: `Can you help me launch my brand on ${marketplaceLabel}?`,
    a: `Yes — brand launch is our #1 service. Timeline ${timeline}. Includes entity setup, trademark filing, dossier prep, buyer pitch, catalog build, hero SKU merchandising, PLA activation.` },
  { q: `Do you handle ${marketplaceLabel} payment reconciliation?`,
    a: `Yes — commission audit, return-cost recovery, TDS handling, dispute filings, penalty reversals. Recovers 3-8% of GMV that most brands leave on the table.` },
  { q: `What if my ${marketplaceLabel} account is suspended?`,
    a: `Account revival is a service we deliver — root cause diagnosis + LTR compliance + policy alignment + appeal drafting + buyer escalation. Median revival: 15-25 days.` },
  { q: `Do you work with startups or only large brands?`,
    a: `Both. Our lightest engagement is ₹40K/month for early D2C brands; enterprise engagements run ₹3-8L/month. Founder-authored playbooks work for ₹5L/month GMV and ₹5Cr/month GMV alike.` },
]);

// ── Marketplace-specific configs ─────────────────────────────────────

const buildMarketplaceCluster = ({
  slug, marketplaceKey, marketplaceLabel, primaryKeyword, secondaryKeywords,
  timelineFallback, commissionRange, brandsLaunchedLabel, mgmtLabel, description,
  gmvManagedLabel, categorySpecific, primaryColor, iconTheme = 'gradient-red',
}) => {
  const mp = m[marketplaceKey] || {};
  const timeline = mp.timeline || timelineFallback;
  const approvalDifficulty = mp.approvalDifficulty || 'Medium-High';
  return {
    slug,
    canonicalPath: `/${slug}`,
    marketplaceKey,
    marketplaceLabel,
    primaryColor,
    iconTheme,
    metaTitle: `${marketplaceLabel} Account Management 2026 — Agency & Growth`,
    metaDescription: `${marketplaceLabel} account management, agency & growth. ${brandsLaunchedLabel}, ${gmvManagedLabel}. Catalog, PPC, PDP, SEO — one 5-member team.`,
    ogImage: `/og/${slug}.png`,
    keywords: [
      primaryKeyword,
      ...secondaryKeywords,
      `${marketplaceLabel} account management`,
      `${marketplaceLabel} seller account management`,
      `${marketplaceLabel} management services`,
      `${marketplaceLabel} agency`,
      `${marketplaceLabel} consultant`,
      `${marketplaceLabel} experts`,
      `${marketplaceLabel} growth services`,
      `${marketplaceLabel} advertising agency`,
      `${marketplaceLabel} SEO services`,
      `${marketplaceLabel} listing services`,
      `${marketplaceLabel} catalog management`,
      `${marketplaceLabel} PDP optimization`,
      `${marketplaceLabel} PLA management`,
      `${marketplaceLabel} sales growth`,
      `${marketplaceLabel} performance marketing`,
      `${marketplaceLabel} brand launch`,
      `${marketplaceLabel} account audit`,
      `${marketplaceLabel} payment reconciliation`,
      `${marketplaceLabel} account health`,
      `${marketplaceLabel} onboarding agency`,
      `${marketplaceLabel} marketplace partner`,
      `${marketplaceLabel} agency india`,
    ].join(', '),
    h1: `${marketplaceLabel} Account Management, Agency & Growth Services`,
    heroSubtitle: description,
    brandsLaunchedLabel,
    gmvManagedLabel,
    mgmtLabel,
    directAnswer: `${marketplaceLabel} account management (${primaryKeyword}) covers day-to-day operations — catalog QC, listing SEO, PLA/PPC advertising, PDP optimization, payment reconciliation, campaign management, and account-health control. Zaroori Retail runs ${marketplaceLabel} accounts for ${brandsLaunchedLabel} through a dedicated ${EEAT_STATS.dedicated_team_size} team model with weekly MOMs. Median engagement drives ${mgmtLabel} within 90 days. Fees start at ₹40K/month for early D2C brands, scale to ₹3-8L/month for enterprise.`,
    categorySpecific,
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Services', url: '/services' },
      { name: `${marketplaceLabel} Account Management`, url: `/${slug}` },
    ],
    faqs: buildMarketplaceFAQs(marketplaceLabel, timeline, commissionRange, approvalDifficulty),
    relatedMarketplaceRegistration: mp.serviceUrls?.brandApproval || mp.serviceUrls?.registration || '/services',
    relatedBlogs: [
      'total-cost-to-launch-brand-indian-marketplaces-2026',
      'marketplace-commission-comparison-india-2026',
      'how-to-sell-on-indian-marketplaces-complete-guide-2026',
    ],
    timeline,
    commissionRange,
    approvalDifficulty,
  };
};

export const MARKETPLACE_TOPIC_CLUSTERS = [
  buildMarketplaceCluster({
    slug: 'myntra-account-management',
    marketplaceKey: 'myntra',
    marketplaceLabel: 'Myntra',
    primaryKeyword: 'Myntra account management',
    secondaryKeywords: ['Myntra agency', 'Myntra consultant', 'Myntra MAM tier optimization', 'Myntra PLA agency'],
    commissionRange: '18-32%',
    brandsLaunchedLabel: '650+ Myntra brands launched',
    gmvManagedLabel: '₹240Cr+ GMV under management',
    mgmtLabel: '2.4-3.8x GMV lift',
    categorySpecific: 'Fashion, footwear, accessories, personal care, beauty, home. MAM (Myntra Active Merchant) tier qualification is a core deliverable — most brands cross Silver tier within month 3 with disciplined SKU/PLA execution.',
    description: 'Full-stack Myntra account management, agency, consultant & growth services for fashion, beauty, footwear, accessories, and home brands. Direct category buyer relationships, MAM-tier qualification, PLA/DSP ROAS optimization, catalog QC, EORS campaign management, and payment reconciliation — delivered by a founder-led 5-member team.',
    primaryColor: 'pink',
  }),
  buildMarketplaceCluster({
    slug: 'nykaa-account-management',
    marketplaceKey: 'nykaa',
    marketplaceLabel: 'Nykaa',
    primaryKeyword: 'Nykaa account management',
    secondaryKeywords: ['Nykaa agency', 'Nykaa consultant', 'Nykaa Pink Pass positioning', 'Nykaa Beauty account management', 'Nykaa Fashion agency'],
    commissionRange: '25-35%',
    brandsLaunchedLabel: '450+ Nykaa brands approved',
    gmvManagedLabel: '₹95Cr+ GMV under management',
    mgmtLabel: '2.1-3.2x GMV lift',
    categorySpecific: 'Beauty (skincare, makeup, haircare, wellness), Nykaa Fashion (jewellery, accessories, ethnic), Nykaa Man (grooming). Pink Pass eligibility and FSSAI + CDSCO compliance are gating criteria we handle end-to-end.',
    description: 'Nykaa Beauty + Nykaa Fashion account management, agency and consultant services. Category buyer pitches, Pink Pass positioning, FSSAI/CDSCO compliance, PLA/DSP optimization, review harvesting, catalog QC, and payment reconciliation for premium beauty and fashion brands.',
    primaryColor: 'pink',
  }),
  buildMarketplaceCluster({
    slug: 'nykaa-beauty-account-management',
    marketplaceKey: 'nykaa',
    marketplaceLabel: 'Nykaa Beauty',
    primaryKeyword: 'Nykaa Beauty account management',
    secondaryKeywords: ['Nykaa Beauty agency', 'Nykaa Beauty consultant', 'Nykaa Pink Pass positioning'],
    commissionRange: '25-35%',
    brandsLaunchedLabel: '340+ Nykaa Beauty brands approved',
    gmvManagedLabel: '₹72Cr+ Nykaa Beauty GMV managed',
    mgmtLabel: '2.3-3.4x beauty-category GMV lift',
    categorySpecific: 'Skincare, makeup, haircare, fragrance, mens grooming, wellness supplements. FSSAI + CDSCO compliance for imported lines. Pink Pass positioning for hero SKUs ≥ ₹399 AOV.',
    description: 'Nykaa Beauty account management + agency + consultant services for skincare, makeup, haircare and wellness brands. Pink Pass positioning, category buyer relationships, FSSAI/CDSCO stack, PLA + DSP campaigns, and content-led PDP optimization.',
    primaryColor: 'pink',
  }),
  buildMarketplaceCluster({
    slug: 'nykaa-fashion-account-management',
    marketplaceKey: 'nykaa',
    marketplaceLabel: 'Nykaa Fashion',
    primaryKeyword: 'Nykaa Fashion account management',
    secondaryKeywords: ['Nykaa Fashion agency', 'Nykaa Fashion consultant'],
    commissionRange: '25-32%',
    brandsLaunchedLabel: '110+ Nykaa Fashion brands approved',
    gmvManagedLabel: '₹23Cr+ Nykaa Fashion GMV managed',
    mgmtLabel: '1.9-2.8x fashion-category GMV lift',
    categorySpecific: 'Ethnic wear, western wear, lingerie, sleep, jewellery, accessories, footwear. Category cross-shelf with Nykaa Beauty gives repeat-purchase lift.',
    description: 'Nykaa Fashion account management + agency services for ethnic, western, jewellery, footwear and accessories brands. Cross-shelf with Nykaa Beauty for repeat-purchase optimization, category buyer relationships, PLA/DSP, and catalog QC.',
    primaryColor: 'pink',
  }),
  buildMarketplaceCluster({
    slug: 'ajio-account-management',
    marketplaceKey: 'ajio',
    marketplaceLabel: 'AJIO',
    primaryKeyword: 'AJIO account management',
    secondaryKeywords: ['AJIO agency', 'AJIO consultant', 'AJIO Luxe agency', 'AJIO Brand Mall management', 'AJIO Indie onboarding'],
    commissionRange: '18-28%',
    brandsLaunchedLabel: '520+ AJIO brands launched',
    gmvManagedLabel: '₹130Cr+ GMV under management',
    mgmtLabel: '2.2-3.5x GMV lift',
    categorySpecific: 'AJIO Brand Mall (mid-premium), AJIO Luxe (luxury ≥₹3,500 AOV), AJIO Indie (emerging D2C). Reliance Retail funding gives sustained PLA depth new brands can lean into.',
    description: 'AJIO account management + agency services across Brand Mall, Luxe and Indie tracks. Reliance Retail buyer relationships, PLA/DSP ROAS optimization, catalog QC, EORS campaign management, catalog-page ranking, and payment reconciliation.',
    primaryColor: 'red',
  }),
  buildMarketplaceCluster({
    slug: 'blinkit-account-management',
    marketplaceKey: 'blinkit',
    marketplaceLabel: 'Blinkit',
    primaryKeyword: 'Blinkit account management',
    secondaryKeywords: ['Blinkit agency', 'Blinkit consultant', 'Blinkit dark-store velocity optimization'],
    commissionRange: '25-40%',
    brandsLaunchedLabel: '210+ Blinkit brands launched',
    gmvManagedLabel: '₹58Cr+ Blinkit GMV managed',
    mgmtLabel: '2.6-4.2x velocity uplift',
    categorySpecific: 'Impulse-buy SKUs at ₹99-₹999 AOV — beauty (lipstick, deodorant, perfume travel), F&B, baby care, personal care, home. Dark-store SKU velocity (10+ units/store/week) is the gating metric.',
    description: 'Blinkit account management + agency services for Quick Commerce brands. Dark-store SKU velocity optimization, hyperlocal PLA, dispatch-hub coordination, damage-in-transit control, and hero-SKU merchandising for impulse-buy categories.',
    primaryColor: 'yellow',
  }),
  buildMarketplaceCluster({
    slug: 'zepto-account-management',
    marketplaceKey: 'zepto',
    marketplaceLabel: 'Zepto',
    primaryKeyword: 'Zepto account management',
    secondaryKeywords: ['Zepto agency', 'Zepto consultant', 'Zepto dark-store optimization'],
    commissionRange: '25-38%',
    brandsLaunchedLabel: '175+ Zepto brands launched',
    gmvManagedLabel: '₹41Cr+ Zepto GMV managed',
    mgmtLabel: '2.4-3.9x velocity uplift',
    categorySpecific: 'Impulse-buy SKUs at ₹99-₹999 AOV. Highest metro coverage among Quick Commerce players. Zepto Café ecosystem cross-shelf potential for F&B brands.',
    description: 'Zepto account management + agency services for Quick Commerce brands. SKU velocity optimization, hyperlocal PLA, Zepto Cafe cross-shelf strategy, campaign management, and category buyer relationships.',
    primaryColor: 'purple',
  }),
  buildMarketplaceCluster({
    slug: 'swiggy-instamart-account-management',
    marketplaceKey: 'instamart',
    marketplaceLabel: 'Swiggy Instamart',
    primaryKeyword: 'Swiggy Instamart account management',
    secondaryKeywords: ['Swiggy Instamart agency', 'Instamart consultant', 'Instamart brand launch'],
    commissionRange: '26-38%',
    brandsLaunchedLabel: '155+ Instamart brands launched',
    gmvManagedLabel: '₹34Cr+ Instamart GMV managed',
    mgmtLabel: '2.2-3.7x velocity uplift',
    categorySpecific: 'F&B, beauty, personal care, baby care, home essentials. Swiggy app cross-traffic (500M+ MAU) drives discovery for new brands. Dinnr / One / Bolt bundled campaigns are a leverage point.',
    description: 'Swiggy Instamart account management + agency services for Quick Commerce brands. Cross-app Swiggy traffic mining, hyperlocal PLA, dark-store velocity, dispatch-hub coordination, and category buyer relationships.',
    primaryColor: 'orange',
  }),
  // Phase X.10 — Amazon entry REMOVED from service clusters (Zaroori Retail does not
  // provide Amazon services). URL /amazon-seller-account-management is preserved as an
  // educational page via leafRoutes → AmazonAccountManagementGuide.
  buildMarketplaceCluster({
    slug: 'pernia-pop-up-shop-account-management',
    marketplaceKey: 'nykaa', // fallback for timeline data; Pernia not in SSOT yet
    marketplaceLabel: 'Pernia\'s Pop-Up Shop',
    primaryKeyword: "Pernia's Pop-Up Shop account management",
    secondaryKeywords: ["Pernia's Pop-Up Shop agency", 'Pernia consultant', 'Pernia designer onboarding'],
    timelineFallback: '25-40 days',
    commissionRange: '25-40%',
    brandsLaunchedLabel: '60+ Pernia designers onboarded',
    gmvManagedLabel: '₹18Cr+ Pernia GMV managed',
    mgmtLabel: '1.9-3.1x GMV lift',
    categorySpecific: 'Luxury ethnic, couture, bridal, jewellery, designer accessories. Handled by our Bridal & Couture desk. High-touch merchandising, editorial styling, buyer curation.',
    description: "Pernia's Pop-Up Shop account management + agency services for luxury ethnic, bridal, couture and designer brands. High-touch designer curation, editorial merchandising, buyer relationships, and post-approval account operations.",
    primaryColor: 'rose',
  }),
  buildMarketplaceCluster({
    slug: 'aza-fashions-account-management',
    marketplaceKey: 'nykaa', // fallback for timeline data
    marketplaceLabel: 'Aza Fashions',
    primaryKeyword: 'Aza Fashions account management',
    secondaryKeywords: ['Aza Fashions agency', 'Aza consultant', 'Aza designer onboarding'],
    timelineFallback: '25-40 days',
    commissionRange: '25-40%',
    brandsLaunchedLabel: '45+ Aza designers onboarded',
    gmvManagedLabel: '₹12Cr+ Aza GMV managed',
    mgmtLabel: '1.9-3.1x GMV lift',
    categorySpecific: 'Luxury ethnic, festive, bridal, jewellery. Editorial-led curation. Designer relationship maintenance is critical.',
    description: 'Aza Fashions account management + agency services for luxury ethnic, bridal and designer brands. Editorial curation, buyer relationships, category merchandising, and full-stack post-approval account operations.',
    primaryColor: 'rose',
  }),
  buildMarketplaceCluster({
    slug: 'tata-cliq-account-management',
    marketplaceKey: 'nykaa', // fallback for timeline
    marketplaceLabel: 'Tata CLiQ',
    primaryKeyword: 'Tata CLiQ account management',
    secondaryKeywords: [
      'Tata CLiQ seller', 'Tata CLiQ seller registration', 'Tata CLiQ onboarding',
      'Tata CLiQ Luxury', 'Tata CLiQ Palette', 'Tata CLiQ vendor',
      'Tata CLiQ consultant', 'Tata CLiQ agency', 'Tata CLiQ marketplace partner',
    ],
    timelineFallback: '25-40 days',
    commissionRange: '15-32%',
    brandsLaunchedLabel: '180+ Tata CLiQ brands onboarded',
    gmvManagedLabel: '₹42Cr+ Tata CLiQ GMV managed',
    mgmtLabel: '2.1-3.3x GMV lift',
    categorySpecific: 'Tata CLiQ Luxury (₹3,500+ AOV designer + luxury fashion), Tata CLiQ Palette (mainstream fashion, beauty, home, electronics). Tata Group buyer team relationships and Neu-loyalty integration are the key onboarding levers.',
    description: 'Tata CLiQ account management, agency and consultant services across Tata CLiQ Luxury and Tata CLiQ Palette. Direct Tata Group buyer team pitch, GST + trademark + brand documentation stack, Neu-loyalty integration, catalog QC, PLA / campaign management, and payment reconciliation. 180+ brands onboarded.',
    primaryColor: 'red',
  }),
];

// ── Category (horizontal) hub configs ─────────────────────────────────

const buildCategoryCluster = ({
  slug, h1, primaryKeyword, secondaryKeywords, metaTitle, metaDescription,
  directAnswer, servicesSubset,
}) => ({
  slug,
  canonicalPath: `/${slug}`,
  category: true,
  primaryColor: 'red',
  iconTheme: 'gradient-red',
  metaTitle,
  metaDescription,
  ogImage: `/og/${slug}.png`,
  keywords: [primaryKeyword, ...secondaryKeywords].join(', '),
  h1,
  heroSubtitle: metaDescription,
  brandsLaunchedLabel: `${EEAT_STATS.brands_served} brands served`,
  gmvManagedLabel: '₹1,200Cr+ combined GMV under management',
  mgmtLabel: '2.1-3.6x GMV lift on average',
  directAnswer,
  categorySpecific: `Cross-marketplace delivery — Myntra, Nykaa, Ajio, Blinkit, Zepto, Swiggy Instamart, Amazon India, Aza Fashions, Pernia's Pop-Up Shop, Tira, FirstCry, BigBasket. One team, one dashboard, one contact.`,
  breadcrumbs: [
    { name: 'Home', url: '/' },
    { name: 'Services', url: '/services' },
    { name: h1, url: `/${slug}` },
  ],
  faqs: buildMarketplaceFAQs('marketplace', '15-45 days', '8-40%', 'Medium-High'),
  relatedMarketplaceRegistration: '/services',
  relatedBlogs: [
    'how-to-sell-on-indian-marketplaces-complete-guide-2026',
    'marketplace-commission-comparison-india-2026',
    'total-cost-to-launch-brand-indian-marketplaces-2026',
  ],
  timeline: '15-45 days',
  commissionRange: '8-40%',
  approvalDifficulty: 'Medium-High',
  servicesSubset,
});

export const CATEGORY_TOPIC_CLUSTERS = [
  buildCategoryCluster({
    slug: 'marketplace-account-management',
    h1: 'Marketplace Account Management Services — 10+ Marketplaces, One Team',
    primaryKeyword: 'marketplace account management',
    secondaryKeywords: ['marketplace management services', 'multi-marketplace agency', 'marketplace management company', 'marketplace account management india'],
    metaTitle: 'Marketplace Account Management 2026 — 10+ Marketplaces, One Team',
    metaDescription: `Marketplace account management, agency & growth services across Myntra, Nykaa, Ajio, Blinkit, Zepto, Instamart, Amazon, Aza, Pernia. ${EEAT_STATS.brands_served} brands. ${EEAT_STATS.specialists} specialists.`,
    directAnswer: `Marketplace account management is the day-to-day operational stack — catalog QC, listing SEO, PLA/PPC advertising, PDP optimization, payment reconciliation, campaign management, review harvesting, account-health control — delivered across all 10+ premium marketplaces. Zaroori Retail runs marketplace accounts for ${EEAT_STATS.brands_served} brands via ${EEAT_STATS.specialists} in-house specialists and a dedicated ${EEAT_STATS.dedicated_team_size} team model per brand. Fees start at ₹40K/month for early D2C brands, scale to ₹3-8L/month for enterprise multi-marketplace engagements.`,
  }),
  buildCategoryCluster({
    slug: 'marketplace-management-company',
    h1: 'Marketplace Management Company — Enterprise Multi-Marketplace Engagement',
    primaryKeyword: 'marketplace management company',
    secondaryKeywords: ['marketplace management company india', 'best marketplace management company', 'top marketplace agency india'],
    metaTitle: 'Marketplace Management Company India 2026 — 10+ Marketplaces | Zaroori Retail',
    metaDescription: `India's leading marketplace management company. ${EEAT_STATS.brands_served} brands served across Myntra, Nykaa, Ajio, Blinkit, Zepto, Amazon. ${EEAT_STATS.specialists} in-house specialists. Founded ${EEAT_STATS.founded_year}.`,
    directAnswer: `Zaroori Retail is India's leading marketplace management company, founded ${EEAT_STATS.founded_year} by ${EEAT_STATS.founder} (${EEAT_STATS.founder_credential}). ${EEAT_STATS.brands_served} brands served, ${EEAT_STATS.specialists} in-house specialists, offices in ${EEAT_STATS.offices}, and 10+ premium marketplaces managed. Enterprise engagements run SLA-backed, dedicated CSMs, 12-month contracts with quarterly OKRs. Weekly MOM reporting standard on every account.`,
  }),
  buildCategoryCluster({
    slug: 'marketplace-consultant',
    h1: 'Marketplace Consultant India — Fractional CMO for Multi-Marketplace Brands',
    primaryKeyword: 'marketplace consultant',
    secondaryKeywords: ['marketplace consultant india', 'ecommerce marketplace consultant', 'best marketplace consultant', 'freelance marketplace consultant'],
    metaTitle: 'Marketplace Consultant India 2026 — Fractional CMO | Zaroori Retail',
    metaDescription: `Marketplace consultant services for D2C brands scaling into Myntra, Nykaa, Ajio, Amazon, Quick Commerce. Fractional CMO, category strategy, buyer meetings.`,
    directAnswer: `A marketplace consultant advises on category strategy, marketplace mix, pricing wars, launch calendars, buyer meetings, and quarterly OKRs — without becoming your full-service agency. Zaroori Retail's consultant desk supports 40+ D2C brands as fractional CMOs, typically 8-16 hours/month at ₹90K-₹1.5L/month retainer. Best fit: brands with an in-house ops team who need buyer relationships + strategic direction.`,
  }),
  buildCategoryCluster({
    slug: 'marketplace-growth-services',
    h1: 'Marketplace Growth Services — Sales, Advertising, Catalog, Retention',
    primaryKeyword: 'marketplace growth services',
    secondaryKeywords: ['marketplace sales growth', 'marketplace growth agency', 'marketplace revenue growth services'],
    metaTitle: 'Marketplace Growth Services 2026 — Sales, PLA, Catalog | Zaroori Retail',
    metaDescription: `Marketplace growth services across Myntra, Nykaa, Ajio, Blinkit, Zepto, Amazon. PLA/DSP ROAS, PDP, catalog QC, campaigns. 2.1-3.6x GMV lift on average.`,
    directAnswer: `Marketplace growth services are the ROAS/GMV outcome-focused engagements — PDP optimization, PLA/DSP advertising, SEO, catalog QC, campaign management, review harvesting, retention loops. Zaroori Retail delivers marketplace growth services for ${EEAT_STATS.brands_served} brands with median GMV lift of 2.1-3.6x within 90 days. Priced ROAS-linked or fixed-retainer depending on maturity.`,
  }),
  buildCategoryCluster({
    slug: 'marketplace-advertising-agency',
    h1: 'Marketplace Advertising Agency — PLA, PPC, DSP, Retargeting',
    primaryKeyword: 'marketplace advertising agency',
    secondaryKeywords: ['marketplace PLA agency', 'marketplace PPC management', 'marketplace advertising services', 'marketplace ads agency india'],
    metaTitle: 'Marketplace Advertising Agency 2026 — PLA, PPC, DSP | Zaroori Retail',
    metaDescription: `Marketplace advertising agency across Myntra PLA, Nykaa PLA, Amazon Ads, Blinkit hyperlocal, Zepto DSP. ROAS-first budget allocation. 3.4x median ROAS on new engagements.`,
    directAnswer: `A marketplace advertising agency runs paid media across marketplace ecosystems — Myntra PLA, Nykaa PLA, Amazon Sponsored Ads / Sponsored Brands / DSP, Blinkit / Zepto hyperlocal PLA. Zaroori Retail deploys ROAS-first budget allocation with weekly bid optimization and negative-keyword hygiene. Median ROAS uplift on new engagements: 1.8x → 3.4x within 90 days. Fees are ROAS-linked or 8-12% of ad spend depending on scale.`,
  }),
  buildCategoryCluster({
    slug: 'marketplace-seo-services',
    h1: 'Marketplace SEO Services — Category Ranking, Long-Tail Keywords, PDP',
    primaryKeyword: 'marketplace SEO services',
    secondaryKeywords: ['marketplace SEO agency', 'marketplace listing SEO', 'Myntra SEO', 'Amazon SEO agency', 'ecommerce SEO india'],
    metaTitle: 'Marketplace SEO Services 2026 — Ranking + Long-Tail | Zaroori Retail',
    metaDescription: `Marketplace SEO services — category page ranking, long-tail keyword capture, PDP optimization, review velocity, search-index audits. Myntra, Nykaa, Ajio, Amazon covered.`,
    directAnswer: `Marketplace SEO is the practice of ranking products in marketplace internal search — Myntra category pages, Amazon SERPs, Nykaa search-index — via long-tail keyword capture, PDP optimization, review velocity, and category-page merchandising. Zaroori Retail delivers marketplace SEO across 10+ platforms with 4-week search-index audits, keyword-loaded titles + descriptions, and category-page ranking uplift for ${EEAT_STATS.brands_served} brands.`,
  }),
  buildCategoryCluster({
    slug: 'marketplace-listing-services',
    h1: 'Marketplace Listing Services — Bulk Uploads, SEO Titles, EAN/GTIN',
    primaryKeyword: 'marketplace listing services',
    secondaryKeywords: ['product listing services', 'marketplace product upload', 'catalog upload services india', 'Myntra listing services', 'Amazon listing services'],
    metaTitle: 'Marketplace Listing Services 2026 — SEO Titles | Zaroori Retail',
    metaDescription: `Marketplace listing services — bulk uploads, SEO titles, EAN/GTIN, variant mapping, MRP hygiene across Myntra, Nykaa, Ajio, Blinkit, Zepto, Amazon.`,
    directAnswer: `Marketplace listing services cover bulk product uploads, SEO-loaded titles, keyword-rich descriptions, EAN/GTIN mapping, variant + swatch mapping, MRP hygiene, HSN + GST coding, and A+ content deployment. Zaroori Retail lists 50,000+ SKUs/month across Myntra, Nykaa, Ajio, Amazon, Blinkit, Zepto. Fees: ₹150-₹450 per SKU basic listing, ₹800-₹2,000 per SKU with A+ content + lifestyle photography.`,
  }),
  buildCategoryCluster({
    slug: 'marketplace-catalog-management',
    h1: 'Marketplace Catalog Management — A+ Content, Image QC, MRP Hygiene',
    primaryKeyword: 'marketplace catalog management',
    secondaryKeywords: ['catalog management services', 'marketplace catalog optimization', 'Myntra catalog management', 'Amazon catalog services', 'A+ content services'],
    metaTitle: 'Marketplace Catalog Management 2026 — A+ Content, QC | Zaroori Retail',
    metaDescription: `Marketplace catalog management across Myntra, Nykaa, Ajio, Amazon, Blinkit, Zepto. A+ content, image QC, MRP hygiene, review Q&A. 50,000+ SKUs/month.`,
    directAnswer: `Marketplace catalog management is the operational backbone — A+ content, image QC to marketplace specs, variant + swatch mapping, MRP hygiene, HSN + GST coding, EAN/GTIN validation, size chart uplift, review Q&A harvesting, and quarterly refresh cadence. Zaroori Retail manages 50,000+ SKUs/month across 10+ marketplaces with 99.4% first-pass QC.`,
  }),
];

// Combined export — everything the template + router consumes.
export const ALL_TOPIC_CLUSTERS = [
  ...MARKETPLACE_TOPIC_CLUSTERS,
  ...CATEGORY_TOPIC_CLUSTERS,
];

// Slug → cluster map for O(1) lookup in template.
export const CLUSTER_BY_SLUG = Object.fromEntries(
  ALL_TOPIC_CLUSTERS.map((c) => [c.slug, c])
);
